// Раздел справочника. Тексты длинные (до ~180 КБ на справочник), поэтому markdown разбирается лениво:
// пока раздел далеко ниже видимой области, на его месте — заглушка расчётной высоты.
// Разделы выше цели перехода рендерятся сразу (eager), поэтому над читаемым местом ничего не «прыгает».
import { useEffect, useRef, useState } from 'react'
import { Markdown } from '../lib/markdown.tsx'
import type { DocSection } from '../types.ts'
import { Icon } from './Icon.tsx'
import { cx } from './ui.tsx'

/** Примерная высота отрисованного текста: ~0.21 px на знак при ширине колонки ~800 px. */
function estimateHeight(chars: number): number {
  return Math.round(48 + chars * 0.21)
}

export function LazyMarkdown({
  text,
  eager,
  className,
  headingBase,
}: {
  text: string
  eager: boolean
  className?: string
  headingBase?: number
}) {
  const [shown, setShown] = useState(eager)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (eager) setShown(true)
  }, [eager])

  useEffect(() => {
    if (shown) return
    const el = ref.current
    if (!el) return
    const root = el.closest('[data-scroll-root]')
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true)
          io.disconnect()
        }
      },
      { root, rootMargin: '1600px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [shown])

  if (shown) return <Markdown text={text} className={className} headingBase={headingBase} />
  return (
    <div ref={ref} aria-hidden="true" className="flex flex-col gap-3 pt-3" style={{ height: estimateHeight(text.length) }}>
      <div className="sk w-[78%]" />
      <div className="sk w-[92%]" />
      <div className="sk w-[64%]" />
    </div>
  )
}

export function DocSectionBlock({
  sec,
  eager,
  onAnchor,
}: {
  sec: DocSection
  eager: boolean
  /** Клик по значку ссылки у заголовка: обновить адрес на #sN. */
  onAnchor?: (no: number) => void
}) {
  return (
    <section
      id={`s${sec.section_no}`}
      data-section={sec.section_no}
      className="scroll-mt-4 border-t border-line pt-7 first:border-t-0 first:pt-0"
      aria-labelledby={`s${sec.section_no}-h`}
    >
      <h2 id={`s${sec.section_no}-h`} className="group flex items-baseline gap-2 text-[19px] leading-snug font-[680] tracking-[-0.01em] text-fg">
        <span className="min-w-0 [overflow-wrap:anywhere]">{sec.section}</span>
        <a
          href={`#s${sec.section_no}`}
          aria-label={`Ссылка на раздел «${sec.section}»`}
          title="Ссылка на раздел"
          className={cx('shrink-0 text-subtle opacity-0 transition-opacity group-hover:opacity-100 hover:text-accent focus-visible:opacity-100')}
          onClick={(e) => {
            if (!onAnchor) return
            e.preventDefault()
            onAnchor(sec.section_no)
          }}
        >
          <Icon name="link" size={14} />
        </a>
      </h2>
      <LazyMarkdown text={sec.body} eager={eager} className="doc mt-3" headingBase={1} />
    </section>
  )
}
